import { faChevronDown, faChevronUp, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useEffect, useState } from "react";
import { Badge, Card, Col, Collapse, ListGroup, Row } from "react-bootstrap";
import { useNavigate } from 'react-router-dom';
import { callQuery, Diagnosis, Note, NOTES_FOR_USERNAME_AND_MRN } from "../../amplify/appsync/query";
import { PatientVisit } from '../../amplify/appsync/search-patients';
import { getHourMinuteDifference, getUserFriendlyDate } from "../../utils/time";
import { callWrite, INSERT_NOTE } from "../../amplify/appsync/write";
import { ExpandingTextarea } from "../input/ExpandingTextArea";
import PersonalIdentifier from "../common/PersonalIdentifier";

export const PatientVisitSelectorItem = ({
    user,
    visit,
    diagnoses,
    patientVisit,
    setShowPatientVisitSelectorModal,
    highlightedPatientVisit,
}: any) => {
    const navigate = useNavigate();

    const [expand, setExpand] = useState<any>({});
    const [isSavingNote, setIsSavingNote] = useState(false);
    const [noteText, setNoteText] = useState("");
    const [saveTimeout, setSaveTimeout] = useState<any>(null);
    const [lastSavedNoteText, setLastSavedNoteText] = useState("");
    const [isNoteTextChanged, setIsNoteTextChanged] = useState(false);


    const navigateToPatientVisit = (visit: PatientVisit) => {
        navigate(`/dashboard/${visit.internal_csn}`);
        setShowPatientVisitSelectorModal(false);
    }

    // Effect to handle autosave on content change
    useEffect(() => {
        // Clear any existing timeout if content changes
        if (saveTimeout) {
            clearTimeout(saveTimeout);
        }

        // Set a new timeout to save the content after 2 seconds of inactivity
        const newTimeout = setTimeout(() => {
            if (isNoteTextChanged) {
                saveNote();
            }
        }, 2000);

        // Store the timeout ID so it can be cleared later
        setSaveTimeout(newTimeout);

        // Clean up the timeout when the component unmounts or content changes
        return () => {
            clearTimeout(newTimeout);
        };
    }, [noteText]); // Only trigger effect when content changes

    useEffect(() => {
        if (expand) {
            callQuery({
                operation: NOTES_FOR_USERNAME_AND_MRN,
                mrn: visit.mrn,
            })
            .then((notes: Note[]) => {
                const filteredNotes = notes.filter(note => note.csn === visit.csn);
                setNoteText(filteredNotes && filteredNotes.length > 0 ? filteredNotes[0].note : "");
            });
        }
    }, [expand]);

    // Effect to handle autosave on content change
    useEffect(() => {
        // Clear any existing timeout if content changes
        if (saveTimeout) {
            clearTimeout(saveTimeout);
        }

        // Set a new timeout to save the content after 2 seconds of inactivity
        const newTimeout = setTimeout(() => {
            if (isNoteTextChanged) {
                saveNote();
            }
        }, 2000);

        // Store the timeout ID so it can be cleared later
        setSaveTimeout(newTimeout);

        // Clean up the timeout when the component unmounts or content changes
        return () => {
            clearTimeout(newTimeout);
        };
    }, [noteText]); // Only trigger effect when content changes

    const handleNoteChange = (text: any) => {
        setIsNoteTextChanged(true);
        setNoteText(text);
    };

    const saveNote = () => {
        if (lastSavedNoteText !== noteText) {
            setIsSavingNote(true);
            callWrite({
                operation: INSERT_NOTE,
                creation_time: new Date().getTime(),
                note_type: "note",
                csn: visit.csn,
                mrn: visit.mrn,
                note: noteText,
            })
            .then((output: Note[]) => {
                console.log("Saved note", output);
                setIsSavingNote(false);
                setNoteText(output[0].note);
                setLastSavedNoteText(output[0].note);
            });
        }
    }

    const formatEncounters = (visit: PatientVisit) => {
        return (
            <>
                {visit.encounter_types?.includes("Emergency") && (
                    <><Badge bg="primary">Emergency Department</Badge>{' '}</>
                )}
                {visit.encounter_types?.includes("Hospital Encounter") && (
                    <><Badge bg="secondary">Hospital Encounter</Badge>{' '}</>
                )}
                {visit.encounter_types?.includes("Outpatient") && (
                    <><Badge bg="secondary">Outpatient</Badge>{' '}</>
                )}
                {visit.encounter_types?.includes("Ambulatory") && (
                    <><Badge bg="secondary">Ambulatory</Badge>{' '}</>
                )}
            </>
        )
    }

    const formatDiagnoses = (visit: PatientVisit) => {
        if (diagnoses) {
            const diagnosesBeforeVisit: any[] = []
            const diagnosesAfterVisit: any[] = []
            for (let diagnosis of diagnoses) {
                if (new Date(diagnosis.noted_date) < new Date(visit.arrival_time)) {
                    diagnosesBeforeVisit.push(diagnosis)
                } else {
                    diagnosesAfterVisit.push(diagnosis)
                }
            }
            return (
                <>
                    <Row>
                        <Col md={12} style={{paddingRight: '4px'}}>
                            <Card className="trackboard-notes-container" style={{cursor: 'default'}} onClick={(e) => e.stopPropagation()}>
                                <Card.Body>
                                    <ExpandingTextarea setText={handleNoteChange} text={noteText} sendChat={() => { } } placeholder={"Add your notes for this patient visit..."} isDisabled={false} />
                                    <Row style={{position: 'absolute', bottom: '12px', right: '12px'}}>
                                        <Col className="text-right">
                                            {
                                                isSavingNote ? (
                                                    <><FontAwesomeIcon icon={faSpinner} spin />{' '}Saving...</>
                                                ) : noteText === lastSavedNoteText ? (
                                                    <>Saved</>
                                                ) : (
                                                    <>Not Saved</>
                                                )
                                            }
                                        </Col>
                                    </Row>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                    {
                        diagnosesBeforeVisit.length > 0 && (
                            <Row className="visit-selector-small">
                                <Col>
                                    <strong>Diagnoses Prior to Visit</strong>
                                    <ListGroup>
                                        {
                                            diagnosesBeforeVisit.map(diagnosis => (
                                                <ListGroup.Item
                                                    key={`diagnosisbefore-${diagnosis.desc10}-${diagnosis.noted_date}`}
                                                    as="li"
                                                >
                                                    <Row>
                                                        <Col md={8}>{diagnosis.desc10}</Col>
                                                        <Col md={4}>{getUserFriendlyDate(diagnosis.noted_date)}</Col>
                                                    </Row>
                                                </ListGroup.Item>
                                            ))
                                        }
                                    </ListGroup>
                                </Col>
                            </Row>
                        )
                    }

                    {
                        diagnosesAfterVisit.length > 0 && (
                            <Row className="visit-selector-small">
                                <Col>
                                    <strong>Diagnoses After Visit</strong>
                                    <ListGroup>
                                        {
                                            diagnosesAfterVisit.map((diagnosis: Diagnosis) => (
                                                <ListGroup.Item
                                                    key={`diagnosisafter-${diagnosis.desc10}-${diagnosis.noted_date}`}
                                                    as="li"
                                                >
                                                    <Row>
                                                        <Col md={8}>{diagnosis.desc10}</Col>
                                                        <Col md={4}>{getUserFriendlyDate(diagnosis.noted_date)}</Col>
                                                    </Row>
                                                </ListGroup.Item>
                                            ))
                                        }
                                    </ListGroup>
                                </Col>
                            </Row>
                        )
                    }

                </>
            )
        } else {
            return (<></>)
        }
    }

    return (
        <ListGroup.Item id={`csn-${visit.csn}`} action key={visit.csn} className={highlightedPatientVisit && highlightedPatientVisit.csn === visit.csn && "visit-selector-highlight"}>
            <Row onClick={() => {navigateToPatientVisit(visit)}}>
                <Col>
                    <Row>
                        <Col md={1}>
                            <Row>
                                <Col>
                                    <strong>{new Date(visit.arrival_time).toLocaleDateString()}</strong>
                                </Col>
                            </Row>
                            <Row>
                                <Col className="visit-selector-small">
                                    {
                                        visit.csn === patientVisit.csn && (
                                            <Badge bg="info">Selected Visit</Badge>
                                        )
                                    }
                                </Col>
                            </Row>
                        </Col>
                        <Col md={1}>
                            <Row>
                                <Col>
                                    <strong>
                                        {
                                            visit.departure_time ? (
                                                <>{getHourMinuteDifference(new Date(visit.arrival_time), new Date(visit.departure_time))}</>
                                            ) : (
                                                <>{getHourMinuteDifference(new Date(visit.arrival_time), new Date())}</>
                                            )
                                        }
                                    </strong>
                                </Col>
                            </Row>
                            <Row>
                                <Col className="visit-selector-small">
                                    {visit.triage_acuity ? visit.triage_acuity : "Unknown Acuity"}
                                </Col>
                            </Row>
                        </Col>
                        <Col md={3}>
                            <Row>
                                <Col>
                                    <strong>
                                        {visit.chief_complaint ? visit.chief_complaint : "-"}
                                    </strong>
                                </Col>
                            </Row>
                            <Row>
                                <Col className="visit-selector-small">
                                    <strong>CSN</strong> <PersonalIdentifier identifier={visit.csn} /> &bull; <strong>Age</strong> {visit.age}
                                </Col>
                            </Row>
                        </Col>
                        <Col md={7}>
                            <Row>
                                <Col>
                                    {
                                        visit.triage_hr && (
                                            <>
                                                <strong>Triage HR</strong> {Math.round(visit.triage_hr)} &bull; <strong>RR</strong> {Math.round(visit.triage_rr)} &bull; <strong>SpO2</strong> {visit.triage_spo2} &bull; <strong>Temp</strong> {visit.triage_temp} &bull; <strong>BP</strong> {Math.round(visit.triage_sbp)}/{Math.round(visit.triage_dbp)}
                                            </>
                                        )
                                    }
                                </Col>
                            </Row>
                            <Row>
                                <Col className="visit-selector-small">
                                    {
                                        formatEncounters(visit)
                                    }{' '}
                                    {
                                        visit.locations ? visit.locations.join(", ") : ""
                                    }
                                </Col>
                            </Row>
                        </Col>
                    </Row>
                </Col>
            </Row>

            {
                expand[visit.csn] ? (
                    <div className="visit-selector-expand" onClick={() => setExpand({...expand, [visit.csn]: false})}><FontAwesomeIcon icon={faChevronUp} /></div>
                ) : (
                    <div className="visit-selector-expand" onClick={() => setExpand({...expand, [visit.csn]: true})}><FontAwesomeIcon icon={faChevronDown} /></div>
                )
            }

            <Collapse in={expand[visit.csn]}>
                {
                    expand[visit.csn] ? (
                        // Prevents memory leak by not trying to render all collapsed items behind the scene when not visible
                        <Row>
                            <Col md={1}></Col>
                            <Col md={11}>
                                {
                                    visit.encounter_types ? (
                                        <Row className="visit-selector-small">
                                            <Col><strong>Encounter Types</strong> {visit.encounter_types.join(", ")}</Col>
                                        </Row>
                                    ) : (<></>)
                                }
                                {visit.llm_summary && (
                                    <Row className="visit-selector-small">
                                        <Col>
                                            {visit.llm_summary ? (
                                                <><strong>Summary</strong> {visit.llm_summary}</>
                                            ) : (
                                                <>-</>
                                            )}
                                        </Col>
                                    </Row>
                                )}
                                {
                                    // TODO: Fix diagnoses with call to actual diagnoses database
                                    // visit.desc10 && visit.desc10.length > 0 && visit.desc10[0] !== "NULL" ? (
                                    //     <Row className="visit-selector-small">
                                    //         <Col><strong>Diagnoses</strong> {visit.desc10.join(", ")}</Col>
                                    //     </Row>
                                    // ) : (<></>)
                                }
                                {
                                    formatDiagnoses(visit)
                                }
                            </Col>
                        </Row>
                    ) : (
                        <></>
                    )
                }
            </Collapse>
        </ListGroup.Item>
    );
};
